import { getSubdomain } from "../../utils/getSubdomain/subdomain";
import { apiAms, createApiClient, handleGetRequest } from "../apiService";
import { editMetadataTypes } from "../articles/articlesTypes";
import { SuccessType } from "../commonTypes";
import { useToken } from "../tokenUtils";
import {
  addBannerTypes,
  addLogoUrlTypes,
  addSupplierLogoTypes,
  editFooterTypes,
  editMetadataResponseTypes,
  editPublicationTypes,
  idResponseTypes,
  editSupplierTypes,
  initialFooterTypes,
  PublicationBannersTypes,
  PublicationContactTypes,
  PublicationPoliciesTypes,
  PublicationTheme,
  PublicationTranslationsTypes,
  publicationTypes,
  requestPublicationTypes,
  supplierTypes,
  userRoleTypes,
  UsersCollection,
} from "./adminTypes";

const domain = getSubdomain();
const apiClient = createApiClient("");

const handleRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.post(url, data, { headers });
  return res.data;
};

const handlePatchRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.patch(url, data, { headers });
  return res.data;
};

const apiClientPublication = createApiClient("admin/publications");
const apiAmsBase = apiAms("ext/");

const getPublications = async (): Promise<publicationTypes[]> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const publications = await handleGetRequest<publicationTypes[]>(
    apiClientPublication,
    "/",
    currentToken,
  );

  return publications;
};

const getPublicationBySlug = async (
  slug: string,
): Promise<publicationTypes> => {
  const response = await apiClientPublication.get<publicationTypes>(`/${slug}`);
  return response.data;
};

const createPublication = async (
  data: requestPublicationTypes,
): Promise<SuccessType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  return handleRequest("/admin/publications/", data, currentToken);
};

const editPublication = async (
  data: editPublicationTypes,
): Promise<publicationTypes> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const res = await handlePatchRequest(
    `/admin/publications/${domain}`,
    data,
    currentToken,
  );
  return res;
};

const deletePublication = async (slug: string): Promise<SuccessType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  if (!slug || typeof slug !== "string") {
    throw new Error(
      "El nombre del proveedor es obligatorio y debe ser un string.",
    );
  }

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClientPublication.delete<SuccessType>(`/${slug}`, {
    headers,
  });
  return res.data;
};

const createArticle = async (data: FormData): Promise<string> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const res = await handleRequest(`${domain}/articles`, data, currentToken);
  return res;
};

const apiClientSupplier = createApiClient("admin/suppliers");

const getSuppliers = async (): Promise<supplierTypes[]> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const suppliers = await handleGetRequest<supplierTypes[]>(
    apiClientSupplier,
    "/",
    currentToken,
  );
  return suppliers;
};

const getSupplier = async (supplier_name: string): Promise<supplierTypes> => {
  const supplier = await handleGetRequest<supplierTypes>(
    apiClientSupplier,
    `/${supplier_name}`,
  );
  return supplier;
};

const editSupplier = async (data: editSupplierTypes): Promise<SuccessType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const res = await handlePatchRequest(
    `/admin/suppliers/${data.name}`,
    data,
    currentToken,
  );
  return res;
};

const deleteSupplier = async (name: string): Promise<SuccessType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  if (!name || typeof name !== "string") {
    throw new Error(
      "El nombre del proveedor es obligatorio y debe ser un string.",
    );
  }

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClientSupplier.delete<SuccessType>(
    `/${encodeURIComponent(name)}`,
    { headers },
  );
  return res.data;
};

const getOrCreateSupplier = async (
  supplier_name: string,
): Promise<supplierTypes> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  if (!supplier_name || typeof supplier_name !== "string") {
    throw new Error(
      "El nombre del proveedor es obligatorio y debe ser un string.",
    );
  }

  const supplier = await handleGetRequest<supplierTypes>(
    apiClientSupplier,
    `/get/${supplier_name}`,
    currentToken,
  );
  return supplier;
};

const editMetadata = async (
  editMetadataRequest: editMetadataTypes,
): Promise<editMetadataResponseTypes> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClient.patch<editMetadataResponseTypes>(
    `${domain}/articles/${editMetadataRequest.article_slug}/edit`,
    editMetadataRequest.data,
    { headers },
  );

  return res.data;
};

const getPublicationStyle = async (): Promise<PublicationTheme> => {
  const res = await handleGetRequest<PublicationTheme>(
    apiClient,
    `/admin/publications/${domain}/theme`,
  );
  return res;
};

export const handlePutFileRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.put(url, data, { headers });
  return res.data;
};

const addLogoUrl = async (data: addLogoUrlTypes): Promise<publicationTypes> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const path = `/admin/publications/${domain}/logo`;
  const res = await handlePutFileRequest(path, data.file, currentToken);
  return res;
};

const addFavicon = async (data: addLogoUrlTypes): Promise<SuccessType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const path = `/admin/publications/${domain}/favicon`;
  const res = await handlePutFileRequest(path, data.file, currentToken);
  return res;
};

const addSupplierLogo = async (
  addSupplierLogoRequest: addSupplierLogoTypes,
): Promise<SuccessType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const path = `/admin/suppliers/${addSupplierLogoRequest.supplier_slug}/logo`;
  const res = await handlePutFileRequest(
    path,
    addSupplierLogoRequest.data.file,
    currentToken,
  );
  return res;
};

const addBanner = async (
  addBannerRequest: addBannerTypes,
): Promise<SuccessType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClient.patch<SuccessType>(
    `/admin/publications/${domain}/banner/${addBannerRequest.banner_number}`,
    addBannerRequest.data,
    { headers },
  );

  return res.data;
};

const addBannerImg = async (addBannerImgRequest: any): Promise<string> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const path = `/admin/publications/${domain}/banner/${addBannerImgRequest.banner_number}/image`;

  const res = await apiClient.put<any>(path, addBannerImgRequest.data, {
    headers,
  });
  return res.data;
};

const getRoles = async (): Promise<userRoleTypes[]> => {
  const currentToken = useToken();
  const res = await handleGetRequest<userRoleTypes[]>(
    apiClient,
    `${domain}/role/all`,
    currentToken || undefined,
  );
  return res;
};

const getRolesByUser = async (): Promise<UsersCollection> => {
  const currentToken = useToken();
  const res = await handleGetRequest<UsersCollection>(
    apiClient,
    `${domain}/role/by_user`,
    currentToken || undefined,
  );
  return res;
};

const getNonStaffRolesByUser = async (): Promise<UsersCollection> => {
  const currentToken = useToken();
  const res = await handleGetRequest<UsersCollection>(
    apiClient,
    `${domain}/role/by_user/non_staff`,
    currentToken || undefined,
  );
  return res;
};

const getPublicationFooter = async (): Promise<PublicationPoliciesTypes> => {
  const response = await apiClientPublication.get<PublicationPoliciesTypes>(
    `/${domain}/footer`,
  );
  return response.data;
};

const getInitialFooter = async (): Promise<initialFooterTypes> => {
  const response = await apiClientPublication.get<initialFooterTypes>(
    `/${domain}/footer_initial`,
  );
  return response.data;
};

const getPublicationContact = async (): Promise<PublicationContactTypes> => {
  const response = await apiClientPublication.get<PublicationContactTypes>(
    `/${domain}/contact`,
  );
  return response.data;
};

const getPublicationBanners = async (): Promise<PublicationBannersTypes> => {
  const response = await apiClientPublication.get<PublicationBannersTypes>(
    `/${domain}/banners`,
  );
  return response.data;
};

const editPublicationFooter = async (
  editFooterRequest: editFooterTypes,
): Promise<idResponseTypes> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClientPublication.patch<idResponseTypes>(
    `/${domain}/footer`,
    editFooterRequest,
    { headers },
  );
  return res.data;
};

const uploadHowToReviewFile = async (
  file: FormData,
): Promise<SuccessType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    "Content-Type": "multipart/form-data",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClientPublication.put<SuccessType>(
    `/${domain}/how-to-review/upload`,
    file,
    { headers },
  );
  return res.data;
};

const deleteHowToReviewFile = async (): Promise<SuccessType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClientPublication.delete<SuccessType>(
    `/${domain}/how-to-review/delete`,
    { headers },
  );
  return res.data;
};

const reparseWord = async (
  article_id: number,
  url: string,
): Promise<string> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    "Content-Type": "application/x-www-form-urlencoded",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const body = new URLSearchParams();
  body.append("url", url);

  const res = await apiAmsBase.post<string>(
    `/articles/${article_id}/reparse-from-url?pub_slug=${domain}`,
    body,
    { headers },
  );
  return res.data;
};

// Idiomas de la revista: language_pub_1 es el principal; code es el codigo (es, en, it...)
interface PublicationLanguage {
  slot: number;
  key: string;
  id: number;
  code: string;
  primary: boolean;
}

const getPublicationLanguages = async (): Promise<PublicationLanguage[]> => {
  const response = await apiClientPublication.get<PublicationLanguage[]>(
    `${domain}/languages`,
  );
  return response.data;
};

// La API entrega las traducciones por posicion (language_pub_1..3, null si no esta
// configurada) y cada bloque trae su idioma (id_language, code). Aqui se pasan a un
// objeto por codigo de idioma que contiene solo los idiomas de la revista, en orden:
// el primero es el principal.
const getPublicationTranslations = async (): Promise<PublicationTranslationsTypes> => {
  const response = await apiClientPublication.get<
    Record<string, (PublicationTranslationsTypes[string] & { id_language?: number; code?: string }) | null>
  >(`${domain}/translations`);

  const byLocale: PublicationTranslationsTypes = {};
  for (const block of Object.values(response.data)) {
    if (!block?.code) continue;
    // id_language y code identifican el idioma; no son campos editables
    const { id_language: _id, code, ...fields } = block;
    byLocale[code] = fields as PublicationTranslationsTypes[string];
  }
  return byLocale;
};

const editOrCreatePublicationTranslations = async (
  translationsData: PublicationTranslationsTypes,
): Promise<SuccessType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  // De codigo de idioma a posicion (language_pub_N); lo que no sea de la revista se descarta
  const languages = await getPublicationLanguages();
  const payload: Record<string, PublicationTranslationsTypes[string]> = {};
  for (const language of languages) {
    const data = translationsData[language.code];
    if (data) payload[language.key] = data;
  }

  const res = await apiClientPublication.patch<SuccessType>(
    `${domain}/translations`,
    payload,
    { headers },
  );
  return res.data;
}

const adminService = {
  reparseWord,
  getPublications,
  getPublicationBySlug,
  createPublication,
  editPublication,
  createArticle,
  deleteSupplier,
  getSuppliers,
  addFavicon,
  addSupplierLogo,
  getSupplier,
  editSupplier,
  deletePublication,
  editMetadata,
  addLogoUrl,
  addBanner,
  addBannerImg,
  uploadHowToReviewFile,
  getRoles,
  getRolesByUser,
  getNonStaffRolesByUser,
  getOrCreateSupplier,
  getPublicationFooter,
  getInitialFooter,
  getPublicationContact,
  getPublicationBanners,
  editPublicationFooter,
  getPublicationStyle,
  deleteHowToReviewFile,
  getPublicationTranslations,
  editOrCreatePublicationTranslations,
};

export default adminService;
